import { inject, observer } from "mobx-react";
import React from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { authStore } from "src/app";
import { ConfirmationDialog, Modal, TextArea, Text, Icon } from "src/components";
import { Deliverable, User } from "src/models";
import { AuthStore, DeliverableStore, MeetingStore, MilestoneStore, QuestionStore, UserStore } from "src/stores";
import { DataType, UserRole } from "src/types";
import { debounce } from "src/util/util";
import { EditData } from ".";
import { CommentView } from "./commentView";
import { FileView } from "./fileView";
import { HistoryView } from "../../../views/log/historyView";
import { RemitView } from "./remitView";
import { theme } from "src/config";
import { format } from "date-fns";
import { enGB, sv } from "date-fns/locale";



export interface IEditDeliverableProps {
  authStore?:      AuthStore,
  delivStore?:     DeliverableStore,
  milestoneStore?: MilestoneStore,
  userStore?:      UserStore,
  meetingStore?:   MeetingStore,
  questionStore?:  QuestionStore,
}

export const EditDeliverable = inject("authStore", "userStore", "delivStore", "meetingStore", "milestoneStore", "questionStore")(observer((props: IEditDeliverableProps): JSX.Element => {
  const { delivStore, userStore, milestoneStore, meetingStore, questionStore } = props;
  const { t, i18n } = useTranslation();
  const [showConfirmationDialog, setShowConfirmationDialog] = React.useState<boolean>(false);
  const [confirmDate, setConfirmDate] = React.useState<Date>(null);
  const [delayReason, setDelayReason] = React.useState<string>(null);
  const ref = React.useRef<{ setActiveView: (view_key: string) => void; }>();
  const d: Deliverable = delivStore.getActive;
  
  const projectLeader = [UserRole.Leader, UserRole.Owner].includes(authStore.getCurUser?.getRole(d.room_id));
  const isCreator = d.created_by == authStore.getCurUserID;
  
  if(!d) return null;
  const isToUser = d.getTo?.id === authStore.getCurUserID;
  const locale = i18n.language == 'sv' ? sv : enGB;

  const editableForCurrentUser = d.canEdit && (projectLeader || isToUser || isCreator);
  console.log( delivStore.linkOptions.getArray.map(o => ({value: o.id, label: o.title})).length )
  return (
    <>
      <EditData 
        ref      = {ref}
        data     = {d}
        canEdit  = {editableForCurrentUser}
        onClose   = {async () => {
          const valid = await d.validate();
          if(!valid.length || !editableForCurrentUser) {
            delivStore.setActive(null);
          } else {
            toast.error(`${t("failed_to_save_item_missing_properties", {type: t('deliverable').toLowerCase()})} ${(valid||[]).map((k:string) => t(k, {ns: 'prop'})).join(', ')} `);
          }
        }}
        subViews = {[
          {iconId: "chat",    key: "chat",    title: t("comment_plural"),   body: <CommentView />,          count: d.total_comments},
          {iconId: "file",    key: "file",    title: t("file_plural"),      body: <FileView    item={d} />,  count: d.total_files},
          {iconId: "history", key: "history", title: t("action_plural"),    body: <HistoryView item={d} />, },
          {iconId: "send",    key: "send",    title: t("send_deliverable"), body: <RemitView   item={d} />, hidden: true},
        ]}
        sections = {[
          {
            title:     t("meeting"),
            hidden:    !meetingStore.getActiveMeeting || !d.isRelevantForMeeting,
            highlight: true,
            entries:   [
              {
                type:     "checkbox",
                title:    t("processed"),
                value:    d.processed,
                disabled: !editableForCurrentUser,
                onClick:  () => d.set("processed", !d.processed, true), 
              },{
                type:     "checkbox",
                title:    t("approved"),
                value:    d.done,
                disabled: !editableForCurrentUser,
                onClick:  () => d.set("done", !d.done, true), 
              }
            ]
          },{
            title: t("attribute_plural"),
            entries: [
              {
                type:     "progressSlider",
                title:    t("progress"),
                hidden:   d.done || !editableForCurrentUser,
                value:    d.progress,
                step:     5,
                onChange: (value) => {
                  if(d.progress > 99 && value < 99) {
                    d.set("delivered", false,  true);
                  } else if(d.progress < 100 && value > 99) {
                    d.set("delivered", true,  true);
                  }
                  d.set("progress", value, true);
                },
              },
              {
                hidden:   !d.delivered || !!meetingStore.getActiveMeeting,
                type:     "checkbox",
                title:    t("approved"),
                value:    d.done,
                disabled: (!editableForCurrentUser || !d.delivered),
                onClick:  () => d.set("done", !d.done, true), 
              },
              { 
                type:     "checkbox",
                title:    t("delivered"),
                value:    d.delivered,
                disabled: !editableForCurrentUser || d.done,
                onClick:  () => {
                  const delivered = !d.delivered;
                  d.set("delivered",  delivered,  true);
                  if(!delivered && d.done) {
                    d.set("done", false, true);
                  }
                  if(!d.delivered && d.progress > 99) {
                    d.set("progress", 75, true);
                  }
                  if(d.delivered && d.progress < 100) {
                    d.set("progress", 100, true);
                  }
                }, 
              },{
                type:       "date",
                title:      t("start_date"),
                value:      d.start_date,
                disabled:   !editableForCurrentUser,
                validRange: { end: d.date },
                onChange:   (value) => {
                  d.set("start_date", value, true)
                }, 
              },{
                type:       "date",
                title:      t("end_date"),
                value:      d.date,
                disabled:   !editableForCurrentUser,
                validRange: { start: d.start_date },
                onChange:   (value) => {
                  if(d.date && !d.isNew && format(d.date, 'yyyy-MM-dd') != format(value, 'yyyy-MM-dd')) {
                    setConfirmDate(value);
                    setShowConfirmationDialog(true);
                  } else {
                    d.set("date", value, true);
                  }
                }
              },
            ]
          },
          {
            title: t("assignee_plural"),
            entries: [
              {
                type:     "user",
                title:    t("owner"),
                value:    d.getTo,
                options:  userStore.getLinkOptions("all"),
                disabled: !editableForCurrentUser,
                onChange: (user: User) => d.set("to", user?.id || null, true),
              },
              {
                type:         "users",
                title:        t("watcher"),
                value:        userStore.getLinks,
                options:      userStore.getLinkOptions("all").filter( o => o.value.id != d.to ),
                disabled:     !editableForCurrentUser,
                isLoading:    userStore.links.getStatus,
                showLoadMore: false,
                onAdd:    (user: User) => userStore.addLink(user.id, "watcher"),
                onRemove: (user: User) => userStore.removeLink(user.id, "watcher"),
                onLoad:   () => {},
              },
            ]
          },{
            title: t("connection_plural"),
            entries: [
              {
                type:         "items",
                title:        t("deliverable_plural"),
                value:        delivStore.links.getArray,
                options:      delivStore.linkOptions.getArray.map(o => ({value: o.id, label: `#${o.number} ${o.title}`})),
                itemType:     DataType.Deliverable,
                isLoading:    delivStore.links.getStatus,
                showLoadMore: delivStore.linkOptions.hasMore,
                disabled:     !editableForCurrentUser,
                onAdd:        (link_id: number) => delivStore.addItemLink(link_id, DataType.Deliverable),
                onRemove:     (link_id: number) => delivStore.removeItemLink(link_id, DataType.Deliverable),
                onLoad:       () =>  delivStore.linkOptions.load(),
                onOpenSelect: (open: boolean) => open ? delivStore.linkOptions.load() : delivStore.linkOptions.clear(),
              },
              {
                type:         "items",
                title:        t("question_plural"),
                value:        questionStore.links.getArray,
                options:      questionStore.linkOptions.getArray.map(o => ({value: o.id, label: `#${o.number} ${o.title}`})),
                itemType:     DataType.Question,
                isLoading:    questionStore.links.getStatus,
                showLoadMore: questionStore.linkOptions.hasMore,
                disabled:     !editableForCurrentUser,
                onAdd:        (link_id: number) => delivStore.addItemLink(link_id, DataType.Question),
                onRemove:     (link_id: number) => delivStore.removeItemLink(link_id, DataType.Question),
                onLoad:       () =>  questionStore.linkOptions.load(),
                onOpenSelect: (open: boolean) => open ? questionStore.linkOptions.load() : questionStore.linkOptions.clear(),
              },
              {
                type:         "items",
                title:        t("milestone_plural"),
                value:        milestoneStore.links.getArray,
                options:      milestoneStore.linkOptions.getArray.map(o => ({value: o.id, label: `#${o.number} ${o.title}`})),
                itemType:     DataType.Milestone,
                isLoading:    milestoneStore.links.getStatus,
                showLoadMore: milestoneStore.linkOptions.hasMore,
                disabled:     !editableForCurrentUser,
                onAdd:        (link_id: number) => delivStore.addItemLink(link_id, DataType.Milestone),
                onRemove:     (link_id: number) => delivStore.removeItemLink(link_id, DataType.Milestone),
                onLoad:       () => milestoneStore.linkOptions.load(),
                onOpenSelect: (open: boolean) => open ? milestoneStore.linkOptions.load() : milestoneStore.linkOptions.clear(),
              }
            ]
          },{
            title: t("option_plural"),
            entries: [
              {
                type:     "action",
                title:    t("send_delivery"),
                onClick:  () => ref?.current?.setActiveView("send"),
                iconId:   "send",
                disabled: !editableForCurrentUser,
              },{
                type:     "action",
                title:    t("link_new_question"),
                onClick:  d.linkNewQuestion,
                iconId:   "add",
                disabled: !editableForCurrentUser,
              },{
                type:     "action",
                title:    t("ics_export"),
                onClick:  d.icsExport,
                iconId:   "calendar",
                disabled: !editableForCurrentUser
              },{
                type:     "action",
                title:    t("remove"),
                hidden:   !d.archived,
                onClick:  async () => { 
                  try { 
                    await d.remove(); delivStore.setActive(null);
                    toast.success(t("deliverable_removed"));
                  } catch (err) {
                    console.error("Failed to remove deliverable", err);
                    toast.error(t("failed_remove_deliverable"))
                  }
                },
                iconId:   "delete",
                disabled: !editableForCurrentUser && !d.archived
              },{
                type:     "action",
                title:    t("archive"),
                hidden:   d.archived,
                onClick:  async () => { 
                  try { 
                    await d.archive();
                    toast.success(t("deliverable_archived"));
                  } catch (err) {
                    console.error("Failed to archive deliverable", err);
                    toast.error(t("failed_archive_deliverable"))
                  }
                },
                iconId:   "archive",
                disabled: !editableForCurrentUser
              }
            ]
          }, 
        ]}
        preTags     = {d.delay_reason ? (
          <div style={{background: theme.color.slate4, padding: 10, margin: 25, marginLeft: 0, marginRight: 0}}>
          <Text size="default" fontStyle='italic' color={theme.color.black}><Icon iconId={'more_time'} size="default" /> <strong>{t("delay_date")}: </strong> {d.delay_date ? format(d.delay_date, 'd MMMM yyyy', {locale}) : null}.</Text>
          <Text size="default" fontStyle='italic' color={theme.color.black}><strong>{t("delay_reason")}: </strong> {d.delay_reason}.</Text>
          </div>
                  
        ) : null}
      />
      <Modal show={showConfirmationDialog}>
        <ConfirmationDialog
          confirmButtonLabel = {t('confirm')}
          disabled           = {!delayReason}
          onCancel           = {() => {
            setShowConfirmationDialog(false)
            setConfirmDate(null);
            setDelayReason(null);
          }}
          label     = {t("new_deliverable_date")}
          message   = {t("reason_for_new_date") + ":"} 
          content   = {
            <TextArea
              defaultValue   = {delayReason}
              onChange       = {(text) => setDelayReason(text)}
              rows           = {5}
              maxLength      = {500}
            />
          }
          onConfirm = {async () => {

            d.set("delay_reason", delayReason, true);
            d.set("delay_date", new Date(), true);
            d.set("date", confirmDate, true);

            setShowConfirmationDialog(false);
            setConfirmDate(null);
            setDelayReason(null);
          }}
        />
      </Modal>
    </>
  )
}));